import { Theme, getContrastRatio } from '@mui/material/styles';
import he from 'he';
import numeral from 'numeral';
import { compositeColors, parseColorToRgba, rgbaToRgbString } from './colorUtils';

export enum BRANDCLUB_SHOPIFY_RETAILERS {
  StoreMyBrandRocks = 100001,
  NatureWise = 100002,
  Gund = 100003,
}

export function isShopifyProduct(retailerId: number): boolean {
  return retailerId > 100000;
}

export const escapeUriPath = (value: unknown, spaceReplacementChar?: string) => {
  let valueString = `${value}`;
  valueString = valueString
    .trim()
    .replace(/[^\w\s]/gi, '')
    .replace(/ +/g, spaceReplacementChar ? spaceReplacementChar : '-');
  return valueString;
};

export const changeAmazonImageUrlResolution = (imageUrl: string, resolution: number): string => {
  // Temporary fix for Sephora
  if (imageUrl.includes('https://www.sephora.comhttps://www.sephora.com')) {
    return imageUrl.replace('https://www.sephora.comhttps://www.sephora.com', 'https://www.sephora.com');
  }

  const reg = /_SL\d+/;
  return imageUrl.includes('media-amazon.com') && imageUrl.includes('_SL')
    ? imageUrl.replace(reg, `_SL${resolution}`)
    : imageUrl;
};

export const isParentProduct = (stacklineSku: string) => {
  const regex = new RegExp('^(?:[0-9]_{1}:?){1}');
  return regex.test(stacklineSku);
};

export const removeTrailingSlash = (str: string): string => {
  return str.endsWith('/') ? str.slice(0, -1) : str;
};


export const isWhite = (color: string) => {
  const patterns = [
    /^white$/, // Word "white"
    /^#fff$/, // Short hex code
    /^#ffffff$/, // Hex code
    /^rgb\(255,\s*255,\s*255\)$/i, // RGB
    /^rgba\(255,\s*255,\s*255,?\s*(1|1\.0*)\)$/i, // RGBA with opacity 1 or omitted
  ];

  return patterns.some((pattern) => pattern.test(color));
};

export const roundFloat = (value: number | string, decimals = 4) => {
  return value ? parseFloat(Number(value).toFixed(decimals)) : 0;
};

export const formatMoney = (num: number, currencySymbol = '$', includeDecimal = true) => {
  return numeral(roundFloat(num)).format(`${currencySymbol}0,0${includeDecimal ? `.00` : ``}`);
};

/**
 * Decode a string of HTML text
 *
 * This function takes a string of HTML and decodes any named
 * and numerical character references
 **/
export const decodeHTMLCharacters = (str: string) => he.decode(str);

/**
 * Returns a color that meets the contrast requirements against the given
 * background. If a `foregroundColor` is supplied, its contrast is verified
 * (taking its alpha channel into account).  When it fails the threshold,
 * the function falls back to `theme.palette.getContrastText(backgroundColor)`.
 *
 * @param theme            MUI theme containing contrast settings
 * @param backgroundColor  Background color to test against
 * @param foregroundColor  Optional preferred foreground color
 */
export const getContrastColor = (theme: Theme, backgroundColor: string, foregroundColor?: string) => {
  const candidate = foregroundColor || theme.palette.primary.main;

  // Parse colors – if parsing fails, defer to MUI's helper.
  const bgRgba = parseColorToRgba(backgroundColor);
  const fgRgba = parseColorToRgba(candidate);

  if (!bgRgba || !fgRgba) {
    return theme.palette.getContrastText(backgroundColor);
  }

  // Blend when the candidate is not fully opaque.
  const effectiveFg = fgRgba.a < 1 ? rgbaToRgbString(compositeColors(bgRgba, fgRgba)) : candidate;

  const contrastRatio = getContrastRatio(backgroundColor, effectiveFg);
  return contrastRatio >= theme.palette.contrastThreshold ? candidate : theme.palette.getContrastText(backgroundColor);
};

export const getNumericValue = (value: string | number | undefined): number | undefined => {
  try {
    const numericValue = typeof value === 'number' ? value : parseFloat(value as string);
    if (Number.isFinite(numericValue)) {
      return numericValue;
    }
  } catch (e) {
    console.warn('Error getting numeric value', e);
  }
};

const DEFAULT_MUI_THEME_FONT_SIZE = 14;
const DEFAULT_BROWSER_HTML_FONT_SIZE = 16;
export const pxToRem = ({
  pxFontSize,
  baseFontSize = DEFAULT_MUI_THEME_FONT_SIZE,
  htmlFontSize = DEFAULT_BROWSER_HTML_FONT_SIZE,
}: {
  pxFontSize: string | number;
  baseFontSize?: number;
  htmlFontSize?: number;
}) => {
  if (typeof pxFontSize === 'string' && pxFontSize.includes('rem')) {
    return pxFontSize;
  }
  const numericPxFontSize = getNumericValue(pxFontSize);
  if (numericPxFontSize == null) {
    console.error('Invalid pxFontSize', pxFontSize);
    return '0rem';
  }
  const coef = baseFontSize / DEFAULT_MUI_THEME_FONT_SIZE;
  return `${(numericPxFontSize / htmlFontSize) * coef}rem` as const;
};

export const remToPx = ({
  remFontSize,
  baseFontSize = DEFAULT_MUI_THEME_FONT_SIZE,
  htmlFontSize = DEFAULT_BROWSER_HTML_FONT_SIZE,
}: {
  remFontSize: number;
  baseFontSize?: number;
  htmlFontSize?: number;
}) => {
  const coef = baseFontSize / DEFAULT_MUI_THEME_FONT_SIZE;
  return remFontSize * htmlFontSize * coef;
};

export const getRootFontSize = () => {
  const rootElement = document.documentElement;
  const rootFontSize = window.getComputedStyle(rootElement).fontSize;
  return getNumericValue(rootFontSize);
};

export const parseThemeUrl = (url: string | undefined) => {
  if (!url) {
    return url;
  }
  if (url.startsWith('url(') && url.endsWith(')')) {
    // remove the url() wrapper
    return url.slice(4, -1);
  }
  return url;
};
